import React, { useState, useEffect, useCallback } from 'react';

interface ScheduleBuilderProps {
  value: string;
  onChange: (cronExpression: string) => void;
  startTime: string; // e.g. '09:00'
}

export const ScheduleBuilder: React.FC<ScheduleBuilderProps> = ({ value, onChange, startTime }) => {
  // Schedule type options
  const SCHEDULE_TYPES = {
    DAILY: 'daily',
    WEEKLY: 'weekly',
    CUSTOM: 'custom',
  };

  // State for the form
  const [scheduleType, setScheduleType] = useState(() => {
    // Parse initial value to determine schedule type
    if (value) {
      const parts = value.split(' ');
      if (parts.length === 5) {
        const dayOfWeekCron = parts[4];
        if (dayOfWeekCron === '*') {
          return SCHEDULE_TYPES.DAILY;
        } else {
          return SCHEDULE_TYPES.WEEKLY;
        }
      }
      return SCHEDULE_TYPES.CUSTOM;
    }
    return SCHEDULE_TYPES.DAILY; // Default to daily only if no value provided
  });
  const [selectedDays, setSelectedDays] = useState<string[]>(() => {
    // Parse initial value to determine selected days
    if (value) {
      const parts = value.split(' ');
      if (parts.length === 5) {
        const dayOfWeekCron = parts[4];
        if (dayOfWeekCron === '*') {
          return [];
        } else {
          let days: string[] = [];
          if (dayOfWeekCron.includes('-')) {
            const [start, end] = dayOfWeekCron.split('-').map(Number);
            for (let i = start; i <= end; i++) {
              days.push(i.toString());
            }
          } else if (dayOfWeekCron.includes(',')) {
            days = dayOfWeekCron.split(',');
          } else {
            days = [dayOfWeekCron];
          }
          return days;
        }
      }
    }
    return ['1', '2', '3', '4', '5']; // Default to Mon-Fri only if no value provided
  });
  const [customCron, setCustomCron] = useState(value || '0 9 * * 1-5');

  // Days of the week
  const daysOfWeek = [
    { value: '0', label: 'Sun' },
    { value: '1', label: 'Mon' },
    { value: '2', label: 'Tue' },
    { value: '3', label: 'Wed' },
    { value: '4', label: 'Thu' },
    { value: '5', label: 'Fri' },
    { value: '6', label: 'Sat' },
  ];

  // Generate cron expression based on UI selections
  const generateCronExpression = useCallback(() => {
    if (scheduleType === SCHEDULE_TYPES.CUSTOM) {
      return customCron;
    }
    
    let cronExpression = '';
    
    // Set minute and hour from startTime
    const [hour, minute] = startTime.split(':');
    cronExpression = `${minute} ${hour}`;
    
    // Add day-of-month, month (always wildcard for repeating schedules)
    cronExpression += ' * *';
    
    // Add day of week
    if (scheduleType === SCHEDULE_TYPES.DAILY) {
      cronExpression += ' *'; // Every day
    } else {
      // Format selected days
      if (selectedDays.length === 0) {
        cronExpression += ' *'; // Fallback to daily if no days selected
      } else if (selectedDays.length === 1) {
        cronExpression += ` ${selectedDays[0]}`; // Single day
      } else {
        // Check if days are consecutive to use range notation
        const sortedDays = [...selectedDays].sort((a, b) => parseInt(a) - parseInt(b));
        let isConsecutive = true;
        
        for (let i = 0; i < sortedDays.length - 1; i++) {
          if (parseInt(sortedDays[i+1]) - parseInt(sortedDays[i]) !== 1) {
            isConsecutive = false;
            break;
          }
        }
        
        if (isConsecutive && sortedDays.length > 2) {
          // Use range notation
          cronExpression += ` ${sortedDays[0]}-${sortedDays[sortedDays.length - 1]}`;
        } else {
          // Use comma-separated list
          cronExpression += ` ${sortedDays.join(',')}`;
        }
      }
    }
    
    return cronExpression;
  }, [startTime, scheduleType, selectedDays, customCron]);

  // Try to parse the initial cron expression
  useEffect(() => {
    // Only update if the value prop changes and is different from our current state
    if (value) {
      try {
        const parts = value.split(' ');
        if (parts.length === 5) {
          const dayOfWeekCron = parts[4];

          // Determine schedule type and selected days based on dayOfWeekCron
          if (dayOfWeekCron === '*') {
            setScheduleType(SCHEDULE_TYPES.DAILY);
            setSelectedDays([]);
          } else {
            setScheduleType(SCHEDULE_TYPES.WEEKLY);
            let days: string[] = [];
            if (dayOfWeekCron.includes('-')) {
              // Handle ranges like 1-5
              const [start, end] = dayOfWeekCron.split('-').map(Number);
              for (let i = start; i <= end; i++) {
                days.push(i.toString());
              }
            } else if (dayOfWeekCron.includes(',')) {
              // Handle comma-separated values
              days = dayOfWeekCron.split(',');
            } else {
              // Single day
              days = [dayOfWeekCron];
            }
            setSelectedDays(days);
          }
          setCustomCron(value);
        } else {
          // If cron expression doesn't match expected format, switch to custom
          setScheduleType(SCHEDULE_TYPES.CUSTOM);
          setCustomCron(value);
        }
      } catch (error) {
        // If parsing fails, just use custom mode
        setScheduleType(SCHEDULE_TYPES.CUSTOM);
        setCustomCron(value);
      }
    }
  }, [value, SCHEDULE_TYPES.DAILY, SCHEDULE_TYPES.WEEKLY, SCHEDULE_TYPES.CUSTOM]); // Only depend on value and constants

  // Effect to update cron when startTime or other relevant schedule params change for non-custom types
  useEffect(() => {
    // Only generate and update if the schedule type is not custom.
    // If it's custom, the cron string is managed by the customCron input field directly via handleCustomCronChange.
    if (scheduleType !== SCHEDULE_TYPES.CUSTOM) {
      // Re-generate cron based on current state and props.
      // generateCronExpression uses `startTime` prop, and `scheduleType`, `selectedDays` state.
      const newCron = generateCronExpression();
      
      // Only call onChange if the newly generated cron is different from the current `value` prop.
      // This helps prevent potential infinite update loops.
      if (newCron !== value) {
        onChange(newCron);
      }
    }
    // Dependencies that should trigger re-generation for non-custom types:
  }, [startTime, scheduleType, selectedDays, value, onChange, generateCronExpression]);

  // Update parent component only when user makes changes
  const handleScheduleTypeChange = (type: string) => {
    setScheduleType(type);
    const newCron = generateCronExpression();
    if (newCron !== value) {
      onChange(newCron);
    }
  };

  const handleCustomCronChange = (newCron: string) => {
    setCustomCron(newCron);
    if (newCron !== value) {
      onChange(newCron);
    }
  };

  const handleDayToggle = (day: string) => {
    const newSelectedDays = selectedDays.includes(day)
      ? selectedDays.filter(d => d !== day)
      : [...selectedDays, day];
    setSelectedDays(newSelectedDays);
    const newCron = generateCronExpression();
    if (newCron !== value) {
      onChange(newCron);
    }
  };

  // Quick selections
  const selectWeekdays = () => {
    setSelectedDays(['1', '2', '3', '4', '5']);
    const newCron = generateCronExpression();
    if (newCron !== value) {
      onChange(newCron);
    }
  };

  const selectWeekends = () => {
    setSelectedDays(['0', '6']);
    const newCron = generateCronExpression();
    if (newCron !== value) {
      onChange(newCron);
    }
  };

  return (
    <div className="space-y-4">
      <div className="text-sm text-gray-600 mb-1">
        Select the type of schedule and customize when the program will occur.
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Schedule Type</label>
        <div className="flex space-x-4">
          <label className="inline-flex items-center">
            <input
              type="radio"
              className="form-radio"
              name="scheduleType"
              value={SCHEDULE_TYPES.DAILY}
              checked={scheduleType === SCHEDULE_TYPES.DAILY}
              onChange={() => handleScheduleTypeChange(SCHEDULE_TYPES.DAILY)}
            />
            <span className="ml-2">Daily</span>
          </label>
          <label className="inline-flex items-center">
            <input
              type="radio"
              className="form-radio"
              name="scheduleType"
              value={SCHEDULE_TYPES.WEEKLY}
              checked={scheduleType === SCHEDULE_TYPES.WEEKLY}
              onChange={() => handleScheduleTypeChange(SCHEDULE_TYPES.WEEKLY)}
            />
            <span className="ml-2">Weekly</span>
          </label>
          <label className="inline-flex items-center">
            <input
              type="radio"
              className="form-radio"
              name="scheduleType"
              value={SCHEDULE_TYPES.CUSTOM}
              checked={scheduleType === SCHEDULE_TYPES.CUSTOM}
              onChange={() => handleScheduleTypeChange(SCHEDULE_TYPES.CUSTOM)}
            />
            <span className="ml-2">Custom Cron</span>
          </label>
        </div>
      </div>

      {scheduleType !== SCHEDULE_TYPES.CUSTOM ? (
        <div className="space-y-4">
          {scheduleType === SCHEDULE_TYPES.WEEKLY && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Days of Week</label>
              <div className="space-y-2">
                <div className="flex flex-wrap gap-2">
                  {daysOfWeek.map(({ value, label }) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => handleDayToggle(value)}
                      className={`px-3 py-1.5 text-sm font-medium rounded-full transition-colors ${
                        selectedDays.includes(value)
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <div className="flex space-x-4 mt-2">
                  <button
                    type="button"
                    onClick={selectWeekdays}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    Weekdays
                  </button>
                  <button
                    type="button"
                    onClick={selectWeekends}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    Weekends
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Custom Cron Expression
            <span className="ml-1 text-xs text-gray-500">(minute hour day-of-month month day-of-week)</span>
          </label>
          <input
            type="text"
            value={customCron}
            onChange={(e) => handleCustomCronChange(e.target.value)}
            className="mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            placeholder="0 9 * * 1-5"
          />
          <div className="mt-1 text-xs text-gray-500">
            Example: "0 9 * * 1-5" means every weekday at 9:00 AM
          </div>
        </div>
      )}
    </div>
  );
}; 